import GraphIcon from '@mui/icons-material/ShareOutlined';
import ListIcon from '@mui/icons-material/ViewListOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Chip, IconButton, Skeleton, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { relationshipsApi, type RelationshipsResponse } from '../../../services/relationships.js';
import type { WidgetProps } from '../types/index.js';

export const RelationshipGraphWidget: React.FC<WidgetProps> = ({ widget }) => {
  const inputRelationships = widget.inputs.find(i => i.id === 'relationships')?.value as any | undefined;
  const location = useLocation();
  const queryProfileId = React.useMemo(() => new URLSearchParams(location.search).get('profileId') || undefined, [location.search]);
  const profileId = ((widget.config as any)?.profileId as string | undefined) || queryProfileId;
  const [fetched, setFetched] = React.useState<RelationshipsResponse | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [mode, setMode] = React.useState<'list'|'graph'>('list');
  const navigate = useNavigate();

  const fetchRelationships = React.useCallback(async (force?: boolean) => {
    let cancelled = false;
    try {
      if (!profileId) return;
      setError(null);
      setLoading(true);
      const data = await relationshipsApi
        .getRelationships(profileId, force ? { force: true } : undefined)
        .catch((e: any) => {
          setError(e?.message || 'Failed to load relationships');
          return {
            suppliers: [],
            customers: [],
            competitors: [],
            lastUpdated: new Date().toISOString(),
          } as RelationshipsResponse;
        });
      if (cancelled) return;
      setFetched(data);
    } finally {
      if (!cancelled) setLoading(false);
    }
    return () => { cancelled = true; };
  }, [profileId]);

  React.useEffect(() => {
    if (inputRelationships) return; // respect provided input
    // Initial load: do NOT force cache busting
    fetchRelationships(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputRelationships, fetchRelationships]);

  // Prefer freshly fetched data (e.g., after a manual refresh) over the initial
  // input pushed in by the agent hydration. Fall back to input if no fetched data yet.
  const relationships = (fetched ?? inputRelationships) ?? undefined;

  if (!relationships) {
    return (
      <Box sx={{ p: 2 }}>
        {!profileId ? (
          <Typography variant="body2" color="text.secondary">
            No profile selected. Open a profile workspace to load relationships.
          </Typography>
        ) : (
          <>
            <Skeleton width={160} />
            <Skeleton width={320} height={180} />
            <Skeleton width={320} height={120} />
          </>
        )}
      </Box>
    );
  }

  const sections: Array<{ key: string; items: any[] }> = [
    { key: 'suppliers', items: relationships.suppliers || [] },
    { key: 'customers', items: relationships.customers || [] },
    { key: 'competitors', items: relationships.competitors || [] },
  ];

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {relationships.lastUpdated ? `Last updated ${new Date(relationships.lastUpdated).toLocaleString()}` : 'Relationships'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Refresh relationships">
            <span>
              <IconButton size="small" onClick={() => fetchRelationships(true)} disabled={loading || !profileId}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_e, v) => v && setMode(v)}>
            <ToggleButton value="list"><ListIcon fontSize="small" /></ToggleButton>
            <ToggleButton value="graph"><GraphIcon fontSize="small" /></ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
      {error && (
        <Typography variant="caption" color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}
      {mode === 'list' ? (
      <Stack spacing={1}>
        {sections.map(sec => (
          <Box key={sec.key}>
            <Typography variant="subtitle2" gutterBottom sx={{ textTransform: 'capitalize' }}>{sec.key}</Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {sec.items.slice(0, 12).map((e: any) => (
                <Chip
                  key={e.id || e.ticker || e.name}
                  label={e.ticker ? `${e.name} (${e.ticker})` : e.name}
                  size="small"
                  onClick={() => {
                    const label = e.ticker ? `${e.name} (${e.ticker})` : e.name || 'this company';
                    const ok = window.confirm(`Open ${label} in a new profile workspace?`);
                    if (!ok) return;
                    if (e.id) {
                      navigate(`/canvas?profileId=${encodeURIComponent(e.id)}`);
                    } else if (e.ticker) {
                      navigate(`/canvas?ticker=${encodeURIComponent(e.ticker)}`);
                    }
                  }}
                />
              ))}
              {sec.items.length === 0 && (
                <Typography variant="caption" color="text.secondary">No {sec.key} found.</Typography>
              )}
              {sec.items.length > 12 && (
                <Chip label={`+${sec.items.length - 12} more`} size="small" variant="outlined" />
              )}
            </Stack>
          </Box>
        ))}
      </Stack>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
          <Typography variant="caption">Graph mode coming soon</Typography>
        </Box>
      )}
    </Box>
  );
};
